import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApi } from "../useApi.js";
import { nf, dateDe } from "../api.js";
import type { DeviceRow } from "../types.js";
import { Page, Pill, statusLabel } from "../components/Shell.js";
import WearRing from "../components/WearRing.js";
import { TableSkeleton } from "../components/Skeleton.js";

const FILTERS = [["ALL", "Alle"], ["CRITICAL", "Kritisch"], ["WARNING", "Achtung"], ["OK", "Bereit"]] as const;

export default function Devices() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = params.get("f") ?? "ALL";
  const [q, setQ] = useState("");
  const { data, error, loading } = useApi<DeviceRow[]>("/api/devices");

  const list = (data ?? [])
    .filter((d) => filter === "ALL" || d.status === filter)
    .filter((d) => !q || `${d.inventoryNo}${d.serialNo}${d.crew}${d.location}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <Page title="Geräte"
          sub={data ? `${list.length} von ${data.length} Geräten` : undefined}
          actions={<>
            <input className="srch" placeholder="Gerät, Seriennummer, Kolonne…"
                   value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="seg">
              {FILTERS.map(([k, l]) => (
                <button key={k} aria-pressed={filter === k}
                        onClick={() => setParams(k === "ALL" ? {} : { f: k })}>{l}</button>
              ))}
            </div>
          </>}>
      {loading && <div className="card"><TableSkeleton /></div>}
      {error && <div className="empty">{error}</div>}
      {data && (
        <div className="card"><div className="tbl-wrap"><table>
          <thead><tr>
            <th>Gerät</th><th>Kolonne / Standort</th><th className="r">Einblasmeter</th>
            <th>Kritischstes Verschleißteil</th><th className="r">DGUV V3</th><th>Zustand</th>
          </tr></thead>
          <tbody>
            {list.length === 0
              ? <tr><td colSpan={6}><div className="empty">Kein Gerät passt zu dieser Auswahl.</div></td></tr>
              : list.map((d) => (
                <tr key={d.id} className="click" onClick={() => nav(`/geraete/${d.id}`)}>
                  <td><b className="mono">{d.inventoryNo}</b><div className="sub-t mono">{d.serialNo}</div></td>
                  <td>{d.crew}<div className="sub-t">{d.location} · FW {d.firmware}</div></td>
                  <td className="r num">{nf(d.totalMeters)} m<div className="sub-t num">{nf(d.totalHours)} Bh</div></td>
                  <td style={{ minWidth: 210 }}>
                    {d.worstWear && <>
                      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                        <WearRing fraction={d.worstWear.fraction} status={d.worstWear.status} size={26} />
                        <span className="num" style={{ fontSize: 11.5, color: "var(--ink-2)" }}>
                          {Math.round(d.worstWear.fraction * 100)} %
                        </span>
                      </div>
                      <div className="sub-t" style={{ marginTop: 2 }}>{d.worstWear.name}</div>
                    </>}
                  </td>
                  <td className="r num">{dateDe(d.nextInspectionAt)}
                    <div className="sub-t">{d.daysToInspection < 0
                      ? `${Math.abs(d.daysToInspection)} T überfällig` : `in ${d.daysToInspection} T`}</div>
                  </td>
                  <td><Pill status={d.status}>{statusLabel(d.status)}</Pill></td>
                </tr>
              ))}
          </tbody>
        </table></div></div>
      )}
    </Page>
  );
}
